/**
 * @file buildStaticCommands.ts
 * @brief Role-aware page jumps, quick actions, and typed filters for Cmd+K.
 *
 * @details
 * Reuses the shell nav tree so palette destinations stay in sync with the
 * mega menu. Entity search results are built separately in
 * `useEntitySearchResults.ts`.
 *
 * @references
 * - layouts/components/nav-utils.ts
 * - layouts/components/superAdminNavConfig.ts
 * - layouts/components/companyNavConfig.ts
 *
 * @author DES
 * @date 17-07-2026
 * @updated 30-07-2026 — Meeting Intel synonym keywords on pages + filters
 */

import {
  Building2,
  CalendarDays,
  Languages,
  CalendarRange,
  FileChartColumn,
  Funnel,
  UserPlus,
  Users,
} from "lucide-react";
import type { NavShellConfig } from "@/layouts/components/nav-types";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { MeetingNotesPen } from "@/components/icons/MeetingNotesPen";
import { flattenGroups } from "@/layouts/components/nav-utils";
import type { CommandPaletteItem } from "./commandPaletteTypes";
import {
  MEETING_INTEL_SYNONYMS,
  meetingRelatedPageKeywords,
} from "./meetingPaletteUtils";

function isoDateDaysAgo(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function dedupeById(items: CommandPaletteItem[]): CommandPaletteItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function pageKeywords(
  label: string,
  description: string | undefined,
  to: string,
): string {
  return [
    "go page navigate",
    label,
    description ?? "",
    meetingRelatedPageKeywords(to),
  ]
    .filter(Boolean)
    .join(" ");
}

export function buildPageCommands(nav: NavShellConfig): CommandPaletteItem[] {
  const fromHighlights = nav.highlights.map((item) => ({
    id: `page:${item.id ?? item.to}`,
    kind: "page" as const,
    label: item.label,
    description: item.description ?? `Go to ${item.label}`,
    keywords: pageKeywords(item.label, item.description, item.to),
    icon: item.icon,
    to: item.to,
  }));

  const fromGroups = flattenGroups(nav.groups).map((item) => ({
    id: `page:${item.id ?? item.to}`,
    kind: "page" as const,
    label: item.label,
    description: item.description ?? `Go to ${item.label}`,
    keywords: pageKeywords(item.label, item.description, item.to),
    icon: item.icon,
    to: item.to,
  }));

  return dedupeById([...fromHighlights, ...fromGroups]);
}

/**
 * A normal user's quick actions (DES 21-10-2026): only those whose page is in
 * their own menu, so a module their company turned off never shows.
 */
export function buildUserActionCommands(nav: NavShellConfig): CommandPaletteItem[] {
  const paths = new Set(buildPageCommands(nav).map((p) => p.to.split(/[?#]/)[0]));
  const has = (to: string) => paths.has(to);
  const actions: CommandPaletteItem[] = [];
  if (has("/app/meetings/new") || has("/app/meetings")) {
    actions.push({
      id: "action:new-meeting",
      kind: "action",
      label: "New meeting",
      description: "Start or schedule a boardroom meeting",
      keywords: "new boardroom meeting record start schedule",
      icon: BoardroomTable,
      to: "/app/meetings/new",
    });
  }
  if (has("/app/notes")) {
    actions.push({
      id: "action:new-note",
      kind: "action",
      label: "Write a note",
      description: "Start a new note",
      keywords: "new note write type notes",
      icon: MeetingNotesPen,
      to: "/app/notes/new",
    });
  }
  if (has("/app/interpret")) {
    actions.push({
      id: "action:interpret",
      kind: "action",
      label: "Start interpreting",
      description: "Open the live interpreter",
      keywords: "interpret interpretation translate live session",
      icon: Languages,
      to: "/app/interpret",
    });
  }
  if (has("/app/schedule")) {
    actions.push({
      id: "action:book-session",
      kind: "action",
      label: "Book a session",
      description: "Open your diary",
      keywords: "book schedule diary appointment interpretation session",
      icon: CalendarDays,
      to: "/app/schedule",
    });
  }
  return actions;
}

export function buildActionCommands(
  isSuperAdmin: boolean,
): CommandPaletteItem[] {
  if (isSuperAdmin) {
    return [
      {
        id: "action:create-user",
        kind: "action",
        label: "Create user",
        description: "Open the new-user dialog",
        keywords: "add new user create",
        icon: UserPlus,
        to: "/admin/users?create=1",
      },
      {
        id: "action:create-company",
        kind: "action",
        label: "Create company",
        description: "Open the new-company dialog",
        keywords: "add new company create tenant",
        icon: Building2,
        to: "/admin/companies?create=1",
      },
    ];
  }

  return [
    {
      id: "action:create-employee",
      kind: "action",
      label: "Create employee",
      description: "Open the new-employee dialog",
      keywords: "add new employee create user",
      icon: UserPlus,
      to: "/company/employees?create=1",
    },
    {
      id: "action:book-session",
      kind: "action",
      label: "Book session",
      description: "Open the company schedule",
      keywords: "schedule book appointment interpretation",
      icon: CalendarDays,
      to: "/company/schedule",
    },
  ];
}

export function buildFilterCommands(
  isSuperAdmin: boolean,
): CommandPaletteItem[] {
  const weekFrom = isoDateDaysAgo(7);

  if (isSuperAdmin) {
    return [
      {
        id: "filter:sessions-week",
        kind: "filter",
        label: "Sessions this week",
        description: "Platform sessions from the last 7 days",
        keywords: "filter sessions week recent",
        icon: CalendarRange,
        to: `/admin/sessions?from=${weekFrom}`,
      },
      {
        id: "filter:users-list",
        kind: "filter",
        label: "Browse users",
        description: "Open the users directory",
        keywords: "filter users directory",
        icon: Users,
        to: "/admin/users",
      },
      {
        id: "filter:companies-list",
        kind: "filter",
        label: "Browse companies",
        description: "Open the companies directory",
        keywords: "filter companies directory",
        icon: Building2,
        to: "/admin/companies",
      },
    {
      id: "filter:meetings",
      kind: "filter",
      label: "Meeting Intel list",
      description: "Platform-wide boardroom meetings",
      keywords: `filter meetings meeting intel boardroom ${MEETING_INTEL_SYNONYMS}`,
      icon: Funnel,
      to: "/admin/meetings",
    },
    {
      id: "filter:reports",
      kind: "filter",
      label: "Reports",
      description: "Exportable revenue, company health, cost vs margin",
      keywords: "filter reports revenue margin company health export csv",
      icon: FileChartColumn,
      to: "/admin/reports",
    },
    ];
  }

  return [
    {
      id: "filter:sessions-week",
      kind: "filter",
      label: "Sessions this week",
      description: "Company sessions from the last 7 days",
      keywords: "filter sessions week recent",
      icon: CalendarRange,
      to: `/company/sessions?from=${weekFrom}`,
    },
    {
      id: "filter:meetings",
      kind: "filter",
      label: "Meeting Intel list",
      description: "Company boardroom meetings",
      keywords: `filter meetings meeting intel boardroom ${MEETING_INTEL_SYNONYMS}`,
      icon: Funnel,
      to: "/company/meetings",
    },
    {
      id: "filter:reports",
      kind: "filter",
      label: "Reports",
      description: "Exportable usage, spend, Meeting Intel, pool burn",
      keywords: `filter reports usage spend pool burn export csv ${MEETING_INTEL_SYNONYMS}`,
      icon: FileChartColumn,
      to: "/company/reports",
    },
  ];
}
